/**
 * Общий редактор для BankReceipt и BankPayment — структура и логика
 * почти идентичные, отличаются типы операций и направление платежа.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bankReceipts, bankPayments,
  type BankReceiptCreate, type BankPaymentCreate, type BasisDocRow, type TaxCandidate,
  type ReceiptOperationType, type PaymentOperationType,
} from '@/api/bankDocs';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { departmentsApi, employees as employeesApi } from '@/api/hr';
import { refs } from '@/api/refs';
import { customerOrders, supplierOrders } from '@/api/orders';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { CFI_BY_OP, OWNER_FUNDS_OPS, opsForOrg } from '@/shared/utils/cfiByOp';
import { currencyOperations } from '@/api/currencyOperations';
import SearchSelect from '@/components/SearchSelect';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import { invalidateRefLists } from '@/shared/utils/refLists';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { ProjectField } from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { findBankByIban, type BankRow } from '@/shared/ukr_classifiers';
import ContractorBankPicker from '@/components/ContractorBankPicker';
import OrgTaxAccountPicker, { orgTaxItems, type OrgTaxItem } from '@/components/OrgTaxAccountPicker';
import TaxPoPicker from '@/components/TaxPoPicker';
import { renderTaxPurposeTemplate } from '@/shared/taxPurposeTemplate';
import { orgOwnCode } from '@/shared/utils/partyCodes';
import { TAX_LINK_LABEL, TAX_LINK_TYPES, purposeWithTaxLinks, type TaxLinkType } from '@/shared/taxLinksPurpose';
import { reports, type SettlementByDocumentRow } from '@/api/documents';
import { useAdvanceInvoiceAction } from '@/shared/hooks/useAdvanceInvoiceAction';
import { goodsSales } from '@/api/goodsSales';
import { productionServiceActs } from '@/api/productionServiceActs';
import { processingServicesSales } from '@/api/processingServicesSales';
import { contractLabel } from '@/shared/utils/labels';
import { createThenPost } from '@/shared/utils/createThenPost';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import { vatPayerOn } from '@/shared/utils/vatFlags';

interface Props {
  kind: 'receipt' | 'payment';
}

const RECEIPT_OPS: ReceiptOperationType[] = [
  'FROM_BUYER', 'FROM_BANK', 'OTHER', 'LOAN', 'CARD_RECEIPT', 'CURRENCY_EXCHANGE',
  'REFUND_FROM_SUPPLIER', 'TRANSFER_OWN', 'CAPITAL_CONTRIBUTION', 'LOAN_GIVEN_RETURN', 'TRANSIT_IN',
  'OWNER_CONTRIBUTION',
];
const PAYMENT_OPS: PaymentOperationType[] = [
  'TO_SUPPLIER', 'TO_BANK', 'TAX', 'SALARY', 'LOAN_RETURN', 'OTHER',
  'CARD_PAYMENT', 'CURRENCY_EXCHANGE', 'REFUND_TO_BUYER', 'TRANSFER_OWN', 'TO_PODOTCHET', 'LOAN_GIVEN', 'TRANSIT_OUT',
  'OWNER_WITHDRAWAL',
];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: string;
  contractor_id: string;
  contract_id: string;
  cash_flow_item_id: string;  // ч.727: стаття ДДС (системні платформи + власні)
  // ч.731: повна картка платіжки (як в 1С УТП).
  counterparty_iban: string;
  counterparty_mfo: string;
  bank_order_number: string;
  paid_at: string;
  department_id: string;
  responsible_name: string;
  tax_purpose_id: string;
  vat_rate_id: string;
  vat_amount: string;
  vat_account_code: string;
  vat_account_confirmed_code: string;
  order_id: string;  // П4a: «сделка» — замовлення/рахунок (deal розрахунків)
  basis_doc_id: string;  // ч.752: «На підставі» — проведена неоплачена накладна (goods_receipt_id / goods_sale_id)
  // ч.908: підстава надходження — акт виробничих послуг (psa) / акт послуг переробки (pss).
  basis_act_kind: '' | 'psa' | 'pss';
  basis_act_id: string;
  basis_hr_id: string;   // ч.754: «На підставі» — відомість виплати ЗП / виплата податків
  tax_po_pdfo_id: string;  // ч.766: платіжки податків (виплата ЗП)
  tax_po_wz_id: string;
  tax_po_esv_id: string;
  recipient_name: string;   // ч.754: отримувач (назва/ПІБ) — у файл банку
  recipient_edrpou: string; // ч.754: код отримувача (ЄДРПОУ / РНОКПП)
  employee_id: string;  // для SALARY — резолв індивід. рах. розрахунків
  amount: string;
  amount_currency: string;  // ч.798: валютний рахунок
  exchange_rate: string;
  payment_purpose: string;
  corresponding_account: string;
  cash_account_code: string;
  comment: string;
  project_id: string | null;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  bank_account_id: '',
  operation_type: '',
  contractor_id: '',
  contract_id: '',
  cash_flow_item_id: '',
  counterparty_iban: '',
  counterparty_mfo: '',
  bank_order_number: '',
  paid_at: '',
  department_id: '',
  responsible_name: '',
  tax_purpose_id: '',
  vat_rate_id: '',
  vat_amount: '0',
  vat_account_code: '',
  vat_account_confirmed_code: '',
  order_id: '',
  basis_doc_id: '',
  basis_act_kind: '',
  basis_act_id: '',
  basis_hr_id: '',
  tax_po_pdfo_id: '',
  tax_po_wz_id: '',
  tax_po_esv_id: '',
  recipient_name: '',
  recipient_edrpou: '',
  employee_id: '',
  amount: '0',
  amount_currency: '',
  exchange_rate: '',
  payment_purpose: '',
  corresponding_account: '',
  cash_account_code: '',
  comment: '',
  project_id: null,
};

export default function BankDocEditor({ kind }: Props) {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  // обе API одинаковой формы; даём типу свободу пройти через мутации
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api: any = kind === 'receipt' ? bankReceipts : bankPayments;
  const listKey = kind === 'receipt' ? 'bank-receipts' : 'bank-payments';
  const itemKey = kind === 'receipt' ? 'bank-receipt' : 'bank-payment';
  const baseRoute = kind === 'receipt' ? '/bank-receipts' : '/bank-payments';
  const opOptions = kind === 'receipt' ? RECEIPT_OPS : PAYMENT_OPS;
  // ч.930 (рішення власника): у списанні тип операції за замовчуванням ПОРОЖНІЙ
  // (бухгалтер обирає свідомо), у надходженні — «від покупця».
  const defaultOp = kind === 'receipt' ? 'FROM_BUYER' : '';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по bank.payment.manage permission (єдиний
  // для receipt + payment).
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('bank.payment.manage');
  const cList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const docQuery = useQuery({
    queryKey: [itemKey, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });
  // ч.1267: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, itemKey, (cid) => api.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  // ч.727: статті ДДС (системні платформи + власні tenant'а).
  const cfiQ = useQuery({ queryKey: ['cash-flow-items'], queryFn: refs.cashFlowItems, staleTime: 300_000 });
  // ч.731: повна картка — підрозділи org, ставки ПДВ, податкові призначення.
  const depQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const vatQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates, staleTime: 300_000 });
  const tpQ = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes, staleTime: 300_000 });
  const baQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id,
  });
  // ч.798: валюта обраного рахунку — для валютних (312) вводимо суму у валюті + курс.
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies, staleTime: 3_600_000 });
  const accountCurrency = useMemo(() => {
    const acc = (baQ.data ?? []).find((b) => b.id === form.bank_account_id);
    return acc ? ((currenciesQ.data ?? []).find((c) => c.id === acc.currency_id)?.code ?? '') : '';
  }, [baQ.data, currenciesQ.data, form.bank_account_id]);
  const isFxAccount = !!accountCurrency && accountCurrency !== 'UAH';
  useEffect(() => {
    // Сума у валюті × курс → сума у грн (лише поки документ не проведено).
    if (isPosted || !isFxAccount) return;
    const a = Number(form.amount_currency), r = Number(form.exchange_rate);
    if (a > 0 && r > 0) {
      const uah = (a * r).toFixed(2);
      if (form.amount !== uah) setForm((f) => ({ ...f, amount: uah }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFxAccount, form.amount_currency, form.exchange_rate]);
  // ч.742: рахунки контрагента (вкладка «банкрахунки» картки) → IBAN/МФО
  // отримувача підставляються з ОСНОВНОГО рахунку при виборі контрагента.
  const ctrBaQ = useQuery({
    queryKey: ['contractor-bank-accounts', form.contractor_id],
    queryFn: () => refs.contractorBankAccounts(form.contractor_id),
    enabled: !!form.contractor_id,
  });
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks() as Promise<BankRow[]>,
    staleTime: 1000 * 60 * 60,
  });
  const ibanAutoFilledFor = useRef<string | null>(null);
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // П4a: замовлення/рахунки контрагента для поля «На підставі» (deal).
  // ч.752: «На підставі» — для розрахунків з контрагентом (не податки/ЗП/банк).
  const showOrderField = kind === 'receipt'
    ? ['FROM_BUYER', 'CARD_RECEIPT', 'OTHER'].includes(form.operation_type)
    : ['TO_SUPPLIER', 'OTHER', 'LOAN_RETURN', 'TO_BANK', 'REFUND_TO_BUYER'].includes(form.operation_type);
  // Проведені неоплачені накладні контрагента (реєстр розрахунків по документах).
  const unpaidDocsQ = useQuery({
    queryKey: ['unpaid-docs', kind, form.organization_id, form.contractor_id],
    queryFn: () => reports.settlementsByDocuments({
      organization_id: form.organization_id, contractor_id: form.contractor_id,
      side: kind === 'receipt' ? 'receivable' : 'payable', limit: 200,
    }),
    enabled: !!form.organization_id && !!form.contractor_id && showOrderField,
  });
  // ч.907 (правило власника): «створити ПН на аванс» неактивна, якщо
  // надходження при рознесенні звʼязане з реалізацією (goods_sale_id) — ПН
  // уже складено за відвантаженням; інакше (рахунок / без підстави) — активна.
  // ч.1447: одразу податкова накладна (підстава — надходження), як у 1С; якщо ПН
  // чи реєстрація авансу вже є — дія відкриває її (useAdvanceInvoiceAction).
  const isBuyerReceipt = kind === 'receipt'
    && ['FROM_BUYER', 'CARD_RECEIPT'].includes(form.operation_type);
  // ч.935 (рішення власника): організація не платник ПДВ — авансова ПН не
  // складається: дія неактивна, підказку ДПС не запитуємо.
  // ч.1383: платник ПДВ на дату документа (статус міг змінитись).
  const docOrg = (orgList.data?.items ?? []).find((o) => o.id === form.organization_id);
  const orgIsVat = !!docOrg && vatPayerOn(docOrg, form.document_date);
  // ч.908: проведені акти покупця — у переліку «На підставі» надходження.
  const actsBasisQ = useQuery({
    queryKey: ['psa-basis', form.organization_id, form.contractor_id],
    queryFn: () => productionServiceActs.list({
      organization_id: form.organization_id, customer_contractor_id: form.contractor_id,
      posted: true, limit: 200,
    }),
    enabled: isBuyerReceipt && !!form.organization_id && !!form.contractor_id,
  });
  const pssBasisQ = useQuery({
    queryKey: ['pss-basis', form.organization_id, form.contractor_id],
    queryFn: () => processingServicesSales.list({
      organization_id: form.organization_id, customer_contractor_id: form.contractor_id,
      posted: true, limit: 200,
    }),
    enabled: isBuyerReceipt && !!form.organization_id && !!form.contractor_id,
  });
  const linkedSaleQ = useQuery({
    queryKey: ['goods-sale-brief', form.basis_doc_id],
    queryFn: () => goodsSales.get(form.basis_doc_id),
    enabled: isBuyerReceipt && !!form.basis_doc_id,
  });
  const { action: advAction } = useAdvanceInvoiceAction({
    receiptType: 'doc.bank_receipts', receiptId: id, organizationId: form.organization_id,
    applicable: isBuyerReceipt, isNew, version: docQuery.dataUpdatedAt,
    blockedReason: !orgIsVat ? 'Організація не є платником ПДВ — податкові накладні не складаються'
      : form.basis_doc_id ? `Надходження рознесено як оплата реалізації${linkedSaleQ.data
        ? ` №${linkedSaleQ.data.document_number} від ${new Date(linkedSaleQ.data.document_date).toLocaleDateString('uk-UA')}`
        : ''} — ПН складено за відвантаженням, аванс не виникає`
      : form.basis_act_id ? 'Надходження рознесено як оплата акта послуг — ПН складено за актом, аванс не виникає'
      : null,
  });
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [taxPickerOpen, setTaxPickerOpen] = useState(false);
  // ч.766: платіжки податків для привʼязки (виплата ЗП): за відомістю «На підставі»,
  // інакше — чернетки «збережених»; сплачені — попап «обрати».
  const [taxPoPicker, setTaxPoPicker] = useState<TaxLinkType | null>(null);
  const isSalaryOp = kind === 'payment' && form.operation_type === 'SALARY';
  const taxCandQ = useQuery({
    queryKey: ['tax-candidates', form.organization_id, isSalaryOp ? form.basis_hr_id : ''],
    queryFn: () => bankPayments.taxCandidates(form.basis_hr_id
      ? { organization_id: form.organization_id, payroll_payment_id: form.basis_hr_id }
      : { organization_id: form.organization_id, posted: false }),
    enabled: !!form.organization_id && isSalaryOp,
  });
  const taxLinkField = (t: TaxLinkType) => `tax_po_${t.toLowerCase()}_id` as 'tax_po_pdfo_id' | 'tax_po_wz_id' | 'tax_po_esv_id';
  const [taxPicked, setTaxPicked] = useState<Record<string, TaxCandidate>>({});
  const taxItemsFor = (t: TaxLinkType) => {
    const cur = form[taxLinkField(t)];
    const list = (taxCandQ.data ?? []).filter((c) => !c.tax_type || c.tax_type === t);
    if (cur && !list.some((c) => c.id === cur) && taxPicked[cur]) list.unshift(taxPicked[cur]);
    return list.map((c) => ({
      id: c.id,
      label: `№ ${c.document_number}${c.document_date ? ' від ' + new Date(c.document_date).toLocaleDateString('uk-UA') : ''}`,
      hint: `${Number(c.amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн${c.posted ? ' · сплачено' : ' · чернетка'}`,
    }));
  };
  // ч.838 (факт власника): збережені привʼязки ПІ (сплачені раніше, № 16/17/18) не
  // входять у список кандидатів (лише чернетки / за відомістю) → пікер показував
  // порожнє поле. Підтягуємо привʼязані документи у taxPicked, щоб підпис був.
  const linkedTaxIds = TAX_LINK_TYPES.map((t) => form[taxLinkField(t)]).filter(Boolean).join(',');
  useEffect(() => {
    if (!isSalaryOp || !linkedTaxIds) return;
    const missing = linkedTaxIds.split(',').filter((id) =>
      !taxPicked[id] && !(taxCandQ.data ?? []).some((c) => c.id === id));
    if (!missing.length) return;
    let alive = true;
    Promise.all(missing.map((id) => bankPayments.get(id).catch(() => null))).then((docs) => {
      if (!alive) return;
      const add: Record<string, TaxCandidate> = {};
      docs.forEach((d) => {
        if (!d) return;
        const t = TAX_LINK_TYPES.find((x) => form[taxLinkField(x)] === d.id) ?? null;
        add[d.id] = { id: d.id, document_number: d.document_number, document_date: d.document_date,
          amount: String(d.amount), posted: !!d.posted, recipient_name: d.recipient_name ?? null, tax_type: t };
      });
      if (Object.keys(add).length) setTaxPicked((prev) => ({ ...prev, ...add }));
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalaryOp, linkedTaxIds, taxCandQ.data]);
  const withTaxPurpose = (f: FormState, picked: Record<string, TaxCandidate>): FormState => {
    const links = TAX_LINK_TYPES.flatMap((t) => {
      const id = f[taxLinkField(t)];
      const c = id ? (picked[id] ?? (taxCandQ.data ?? []).find((x) => x.id === id)) : undefined;
      return c ? [{ tax_type: t, document_number: c.document_number, document_date: c.document_date }] : [];
    });
    return { ...f, payment_purpose: purposeWithTaxLinks(f.payment_purpose, links) };
  };
  const pickTaxPo = (t: TaxLinkType, c: TaxCandidate | null) => {
    const picked = c ? { ...taxPicked, [c.id]: c } : taxPicked;
    setTaxPicked(picked);
    setForm(withTaxPurpose({ ...form, [taxLinkField(t)]: c?.id ?? '' }, picked));
  };
  const currentOrg = (orgList.data?.items ?? []).find((o) => o.id === form.organization_id);
  // ч.1133: «власні кошти підприємця» — лише для ФОП; уже обраний вид у відкритому
  // документі лишаємо у списку, щоб поле не стало порожнім.
  const visibleOps: string[] = opsForOrg<string>(opOptions, currentOrg?.entity_type);
  if (form.operation_type && !visibleOps.includes(form.operation_type)) visibleOps.push(form.operation_type);
  const taxItems = orgTaxItems(currentOrg);
  // обраний податок — за IBAN/отримувачем (у документі не зберігається)
  const taxValue = taxItems.find((x) =>
    (x.recipient_iban && x.recipient_iban === form.counterparty_iban)
    || (!x.recipient_iban && x.recipient_name === form.recipient_name && !!x.recipient_name))?.key ?? '';
  const pickTax = (it: OrgTaxItem | undefined) => {
    if (!it) { setForm({ ...form, recipient_name: '', recipient_edrpou: '', counterparty_iban: '', counterparty_mfo: '' }); return; }
    const iban = (it.recipient_iban ?? '').replace(/\s+/g, '').toUpperCase();
    setForm({ ...form,
      recipient_name: it.recipient_name ?? '', recipient_edrpou: it.recipient_edrpou ?? '',
      counterparty_iban: iban, counterparty_mfo: mfoOf(iban) ?? '',
      payment_purpose: form.payment_purpose || (it.purpose_template
        ? renderTaxPurposeTemplate(it.purpose_template, {
            tax_type: it.tax_type ?? it.name, tax_label: it.name, edrpou: orgOwnCode(currentOrg),
            year: new Date(form.document_date).getFullYear(), month: new Date(form.document_date).getMonth() + 1,
            org: currentOrg?.name ?? null })
        // ч.1281: без шаблону — код виду сплати 101 і назва податку (період бухгалтер допише)
        : `101 ${it.name}`) });
  };
  // ч.840 (рішення власника): зміна виду операції / статті ДДС більше НЕ очищає
  // поля «Інструкції» (скасовано правило ч.757 п.3).
  const mfoOf = (iban: string) => {
    const c = iban.replace(/\s+/g, '').toUpperCase();
    return c.length >= 10 ? c.slice(4, 10) : null;
  };
  const ordersApi = kind === 'receipt' ? customerOrders : supplierOrders;
  const dealOrdersQ = useQuery({
    queryKey: ['deal-orders', kind, form.organization_id, form.contractor_id],
    queryFn: () => ordersApi.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      posted: true, limit: 100,
    }),
    enabled: !!form.organization_id && !!form.contractor_id && showOrderField,
  });
  // Виплата ЗП (SALARY) — отримувач = працівник (у т.ч. звільнений) у полі
  // «Отримувач»; резолв індивід. рах. розрахунків через employee_id (ч.754).
  const showEmployee = kind === 'payment' && form.operation_type === 'SALARY';
  const isSalary = kind === 'payment' && form.operation_type === 'SALARY';
  // ч.827: отримувач — працівник також для «у підзвіт» (372).
  const isEmployeeOp = isSalary || (kind === 'payment' && form.operation_type === 'TO_PODOTCHET');
  const isTax = kind === 'payment' && form.operation_type === 'TAX';
  // ч.828: договір лише для розрахунків з контрагентом (не податки/ЗП/підзвіт/переказ між своїми).
  const noContract = isEmployeeOp || isTax || form.operation_type === 'TRANSFER_OWN'
    || OWNER_FUNDS_OPS.includes(form.operation_type)                            // ч.1133
    || (kind === 'receipt' && form.operation_type === 'CAPITAL_CONTRIBUTION');  // ч.835
  const hrBasisQ = useQuery({
    queryKey: ['bank-basis-docs', form.organization_id, isSalary ? 'SALARY' : 'TAX'],
    queryFn: () => bankPayments.basisDocs(form.organization_id, isSalary ? 'SALARY' : 'TAX'),
    enabled: !!form.organization_id && (isSalary || isTax),
  });
  const empQ = useQuery({
    queryKey: ['bank-employees', form.organization_id],
    queryFn: () => employeesApi.list({
      organization_id: form.organization_id, active_only: false, limit: 1000,
    }),
    enabled: !!form.organization_id && showEmployee,
  });

  // ч.740: «створити на підставі» з документів Покупок — query-параметри
  // (org/contractor/contract/amount/purpose/order/project/op/corr).
  const [sp] = useSearchParams();
  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: sp.get('org') || firstOrg,
        operation_type: sp.get('op') || f.operation_type || defaultOp,
        contractor_id: sp.get('contractor') || f.contractor_id,
        contract_id: sp.get('contract') || f.contract_id,
        order_id: sp.get('order') || f.order_id,
        amount: sp.get('amount') || f.amount,
        payment_purpose: sp.get('purpose') || f.payment_purpose,
        project_id: sp.get('project') || f.project_id,
        corresponding_account: sp.get('corr') || f.corresponding_account,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      // збережений документ — реквізити не перезаписуємо авто-підстановкою
      ibanAutoFilledFor.current = d.contractor_id ?? '';
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        bank_account_id: d.bank_account_id,
        amount_currency: (d as { amount_currency?: string | null }).amount_currency ?? '',
        exchange_rate: (d as { exchange_rate?: string | null }).exchange_rate ?? '',
        operation_type: d.operation_type,
        contractor_id: d.contractor_id ?? '',
        contract_id: d.contract_id ?? '',
        cash_flow_item_id: (d as { cash_flow_item_id?: string | null }).cash_flow_item_id ?? '',
        counterparty_iban: (kind === 'receipt'
          ? (d as { payer_iban?: string | null }).payer_iban
          : (d as { recipient_iban?: string | null }).recipient_iban) ?? '',
        counterparty_mfo: (kind === 'receipt'
          ? (d as { payer_bank_mfo?: string | null }).payer_bank_mfo
          : (d as { recipient_bank_mfo?: string | null }).recipient_bank_mfo) ?? '',
        bank_order_number: (d as { bank_order_number?: string | null }).bank_order_number ?? '',
        paid_at: d.paid_at ? d.paid_at.slice(0, 10) : '',
        department_id: d.department_id ?? '',
        responsible_name: d.responsible_name ?? '',
        tax_purpose_id: d.tax_purpose_id ?? '',
        vat_rate_id: d.vat_rate_id ?? '',
        vat_amount: String(d.vat_amount ?? '0'),
        vat_account_code: d.vat_account_code ?? '',
        vat_account_confirmed_code: d.vat_account_confirmed_code ?? '',
        order_id: (kind === 'receipt'
          ? d.customer_order_id : d.supplier_order_id) ?? '',
        basis_doc_id: (kind === 'receipt'
          ? (d as { goods_sale_id?: string | null }).goods_sale_id
          : (d as { goods_receipt_id?: string | null }).goods_receipt_id) ?? '',
        basis_act_kind: kind === 'receipt'
          ? ((d as { production_service_act_id?: string | null }).production_service_act_id ? 'psa'
            : (d as { processing_services_sale_id?: string | null }).processing_services_sale_id ? 'pss' : '')
          : '',
        basis_act_id: kind === 'receipt'
          ? ((d as { production_service_act_id?: string | null }).production_service_act_id
            ?? (d as { processing_services_sale_id?: string | null }).processing_services_sale_id ?? '')
          : '',
        basis_hr_id: (kind === 'payment'
          ? ((d as { payroll_payment_id?: string | null }).payroll_payment_id
            ?? (d as { tax_remittance_id?: string | null }).tax_remittance_id)
          : null) ?? '',
        tax_po_pdfo_id: (d as { tax_po_pdfo_id?: string | null }).tax_po_pdfo_id ?? '',
        tax_po_wz_id: (d as { tax_po_wz_id?: string | null }).tax_po_wz_id ?? '',
        tax_po_esv_id: (d as { tax_po_esv_id?: string | null }).tax_po_esv_id ?? '',
        recipient_name: (kind === 'receipt'
          ? (d as { payer_name?: string | null }).payer_name
          : (d as { recipient_name?: string | null }).recipient_name) ?? '',
        recipient_edrpou: (kind === 'receipt'
          ? (d as { payer_edrpou?: string | null }).payer_edrpou
          : (d as { recipient_edrpou?: string | null }).recipient_edrpou) ?? '',
        employee_id: ('employee_id' in d ? d.employee_id : null) ?? '',
        amount: String(d.amount),
        payment_purpose: d.payment_purpose ?? '',
        corresponding_account: (d as { corresponding_account?: string | null })
          .corresponding_account ?? '',
        cash_account_code: (d as { cash_account_code?: string | null })
          .cash_account_code ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка до замовлень і підстав
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), bank_order_number: EMPTY.bank_order_number, paid_at: EMPTY.paid_at, order_id: EMPTY.order_id, basis_doc_id: EMPTY.basis_doc_id, basis_act_kind: EMPTY.basis_act_kind, basis_act_id: EMPTY.basis_act_id, basis_hr_id: EMPTY.basis_hr_id, tax_po_pdfo_id: EMPTY.tax_po_pdfo_id, tax_po_wz_id: EMPTY.tax_po_wz_id, tax_po_esv_id: EMPTY.tax_po_esv_id, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultOp]);

  // подставить главный счёт по умолчанию
  useEffect(() => {
    if (isNew && !form.bank_account_id && baQ.data && baQ.data.length > 0) {
      const main = baQ.data.find((b) => b.is_main) ?? baQ.data[0];
      if (main) setForm((f) => ({ ...f, bank_account_id: main.id }));
    }
  }, [isNew, baQ.data, form.bank_account_id]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // ч.742: при зміні контрагента — IBAN + МФО з його основного банк-рахунку.
  useEffect(() => {
    if (isPosted || !form.contractor_id) return;
    if (ibanAutoFilledFor.current === form.contractor_id) return;
    if (!ctrBaQ.data) return;
    ibanAutoFilledFor.current = form.contractor_id;
    const main = ctrBaQ.data.find((b) => b.is_main) ?? ctrBaQ.data[0];
    if (!main) return;
    const iban = main.iban.replace(/\s+/g, '').toUpperCase();
    setForm((f) => ({ ...f, counterparty_iban: iban, counterparty_mfo: iban.slice(4, 10) }));
  }, [isPosted, form.contractor_id, ctrBaQ.data]);
  const counterpartyBankName = findBankByIban(form.counterparty_iban, banksQ.data ?? []);
  // ч.766: за відомістю є рівно одна платіжка кожного податку → підставити (нова платіжка).
  useEffect(() => {
    if (!isSalary || isPosted || !form.basis_hr_id || !taxCandQ.data) return;
    let next: FormState = form; let changed = false;
    for (const t of TAX_LINK_TYPES) {
      const key = taxLinkField(t);
      if (next[key]) continue;
      const lst = taxCandQ.data.filter((c) => c.tax_type === t);
      const one = lst.length === 1 ? lst[0] : undefined;
      if (one) { next = { ...next, [key]: one.id }; changed = true; }
    }
    if (changed) setForm(withTaxPurpose(next, taxPicked));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalary, isPosted, form.basis_hr_id, taxCandQ.data]);
  // ч.757 (п.1): договір — основний з контрагентом (або єдиний) авто, якщо не обрано.
  useEffect(() => {
    if (isPosted || noContract || !form.contractor_id || form.contract_id || !contractsQ.data) return;
    const list = contractsQ.data;
    const main = list.find((c) => c.is_main) ?? (list.length === 1 ? list[0] : undefined);
    if (main) setForm((f) => (f.contract_id ? f : { ...f, contract_id: main.id }));
  }, [isPosted, form.contractor_id, form.contract_id, contractsQ.data]);
  // ч.752: стаття ДДС — авто за типом операції (системні статті платформи);
  // юзер може змінити. Перезаписуємо лише порожню або попередньо авто-підставлену.
  const cfiAutoRef = useRef<string>('');
  useEffect(() => {
    if (isPosted || !cfiQ.data) return;
    const code = CFI_BY_OP[form.operation_type];
    const auto = code ? cfiQ.data.find((x) => x.is_system && x.code === code)?.id ?? '' : '';
    if (!auto) return;
    if (!form.cash_flow_item_id || form.cash_flow_item_id === cfiAutoRef.current) {
      cfiAutoRef.current = auto;
      if (form.cash_flow_item_id !== auto) setForm((f) => ({ ...f, cash_flow_item_id: auto }));
    }
  }, [isPosted, form.operation_type, cfiQ.data, form.cash_flow_item_id]);
  // ч.753: сума ПДВ — авто з суми і ставки (ПДВ у сумі платежу: S × r / (100 + r));
  // перезаписуємо лише порожню / попередньо авто-розраховану — ручну правку лишаємо.
  const vatAutoRef = useRef<string>('');
  useEffect(() => {
    if (isPosted) return;
    const rate = (vatQ.data ?? []).find((v) => v.id === form.vat_rate_id)?.rate_percent;
    const amt = Number(form.amount);
    if (rate === undefined || !(amt > 0)) return;
    const auto = (amt * rate / (100 + rate)).toFixed(2);
    const cur = form.vat_amount;
    if (!cur || Number(cur) === 0 || cur === vatAutoRef.current) {
      vatAutoRef.current = auto;
      if (cur !== auto) setForm((f) => ({ ...f, vat_amount: auto }));
    }
  }, [isPosted, form.amount, form.vat_rate_id, vatQ.data, form.vat_amount]);
  // ч.754: ставка ПДВ за замовчуванням 20% (новий документ, поки не обрано).
  useEffect(() => {
    if (!isNew || form.vat_rate_id || !vatQ.data) return;
    const r20 = vatQ.data.find((v) => Number(v.rate_percent) === 20 && v.is_active !== false);
    if (r20) setForm((f) => (f.vat_rate_id ? f : { ...f, vat_rate_id: r20.id }));
  }, [isNew, vatQ.data, form.vat_rate_id]);
  // ч.753: номер платіжки — єдиний для нас і банку (наступний числовий).
  const nextNumQ = useQuery({
    queryKey: ['bank-payment-next-number', form.organization_id],
    queryFn: () => bankPayments.nextNumber(form.organization_id),
    enabled: isNew && kind === 'payment' && !!form.organization_id,
  });
  const cfiItems = (cfiQ.data ?? []).map((x) => ({
    id: x.id, label: x.name,
    hint: x.flow_type === 'OPERATING' ? 'операційна' : x.flow_type === 'INVESTING' ? 'інвестиційна' : 'фінансова',
  }));
  // «На підставі»: відкриті замовлення/рахунки + проведені неоплачені накладні.
  const basisItems = [
    ...(dealOrdersQ.data?.items ?? [])
      .filter((o: any) => (o.closing_status ?? 'OPEN') !== 'CLOSED' && o.state !== 'CANCELLED')
      .map((o: any) => ({
        id: `order:${o.id}`,
        label: `${o.order_kind === 'PROFORMA_INVOICE' ? 'Рахунок' : 'Замовлення'} ${o.document_number} від ${new Date(o.document_date).toLocaleDateString('uk-UA')}`,
        hint: `${Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
      })),
    ...(unpaidDocsQ.data?.items ?? []).map((r: SettlementByDocumentRow) => ({
      id: `doc:${r.settlement_doc_id}`,
      label: `${kind === 'receipt' ? 'Реалізація' : 'Надходження'} ${r.document_number ?? ''}${r.document_date ? ' від ' + new Date(r.document_date).toLocaleDateString('uk-UA') : ''}`,
      hint: `не сплачено ${Number(r.open_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
    })),
    // ч.908: акти виробничих послуг / послуг переробки (проведені).
    ...(kind === 'receipt' ? (actsBasisQ.data?.items ?? []).map((a: any) => ({
      id: `psa:${a.id}`,
      label: `Акт виробничих послуг ${a.document_number ?? ''} від ${new Date(a.document_date).toLocaleDateString('uk-UA')}`,
      hint: `${Number(a.total_amount ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
    })) : []),
    ...(kind === 'receipt' ? (pssBasisQ.data?.items ?? []).map((a: any) => ({
      id: `pss:${a.id}`,
      label: `Акт послуг переробки ${a.document_number ?? ''} від ${new Date(a.document_date).toLocaleDateString('uk-UA')}`,
      hint: `${Number(a.total_amount ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
    })) : []),
  ];
  const fmtD = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('uk-UA') : '');
  const hrBasisItems = (hrBasisQ.data ?? []).map((r: BasisDocRow) => ({
    id: `hr:${r.id}`,
    label: `${r.doc_type === 'doc.payroll_payments' ? 'Виплата зарплати' : 'Виплата податків'} № ${r.document_number}${r.document_date ? ' від ' + fmtD(r.document_date) : ''}${r.period_year ? ` (${String(r.period_month).padStart(2, '0')}.${r.period_year})` : ''}`,
    hint: `не сплачено ${Number(r.open_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн`,
  }));
  const basisValue = form.order_id ? `order:${form.order_id}`
    : form.basis_doc_id ? `doc:${form.basis_doc_id}`
    : form.basis_act_id ? `${form.basis_act_kind}:${form.basis_act_id}`
    : form.basis_hr_id ? `hr:${form.basis_hr_id}` : '';
  // ч.754 (п.9): підстава обрана → призначення платежу = «Оплата … згідно з <документ>».
  const pickBasis = (v: string) => {
    if (!v) { setForm({ ...form, order_id: '', basis_doc_id: '', basis_act_kind: '', basis_act_id: '', basis_hr_id: '' }); return; }
    const kindKey = v.slice(0, v.indexOf(':'));
    const id = v.slice(v.indexOf(':') + 1);
    if (kindKey === 'order') {
      const o: any = (dealOrdersQ.data?.items ?? []).find((x: any) => x.id === id);
      const open = o ? Number(o.total_amount) - Number(o.paid_amount ?? 0) : 0;
      const isInv = o?.order_kind === 'PROFORMA_INVOICE';
      setForm({
        ...form, order_id: id, basis_doc_id: '', basis_act_kind: '', basis_act_id: '', basis_hr_id: '',
        amount: !Number(form.amount) && open > 0 ? open.toFixed(2) : form.amount,
        contract_id: form.contract_id || (o?.contract_id ?? ''),
        payment_purpose: o ? `Оплата згідно з ${isInv ? 'рахунком' : 'замовленням'} № ${o.document_number} від ${fmtD(o.document_date)}` : form.payment_purpose,
      });
      return;
    }
    if (kindKey === 'psa' || kindKey === 'pss') {
      const list: any[] = (kindKey === 'psa' ? actsBasisQ.data?.items : pssBasisQ.data?.items) ?? [];
      const a = list.find((x) => x.id === id);
      setForm({
        ...form, basis_act_kind: kindKey, basis_act_id: id, order_id: '', basis_doc_id: '', basis_hr_id: '',
        amount: !Number(form.amount) && a ? Number(a.total_amount ?? 0).toFixed(2) : form.amount,
        contract_id: form.contract_id || (a?.contract_id ?? ''),
        payment_purpose: a
          ? `Оплата за послуги згідно з актом № ${a.document_number ?? ''} від ${fmtD(a.document_date)}`
          : form.payment_purpose,
      });
      return;
    }
    if (kindKey === 'hr') {
      const r = (hrBasisQ.data ?? []).find((x) => x.id === id);
      const per = r?.period_year ? ` за ${String(r.period_month).padStart(2, '0')}.${r.period_year}` : '';
      setForm({
        ...form, basis_hr_id: id, order_id: '', basis_doc_id: '', basis_act_kind: '', basis_act_id: '',
        amount: !Number(form.amount) && r ? Number(r.open_amount).toFixed(2) : form.amount,
        payment_purpose: r ? (r.doc_type === 'doc.payroll_payments'
          ? `Виплата заробітної плати${per} згідно з відомістю № ${r.document_number} від ${fmtD(r.document_date)}`
          : `Перерахування податків із заробітної плати${per} згідно з документом № ${r.document_number} від ${fmtD(r.document_date)}`) : form.payment_purpose,
      });
      return;
    }
    const r = (unpaidDocsQ.data?.items ?? []).find((x) => x.settlement_doc_id === id);
    setForm({
      ...form, basis_doc_id: id, order_id: '', basis_act_kind: '', basis_act_id: '', basis_hr_id: '',
      amount: !Number(form.amount) && r ? Number(r.open_amount).toFixed(2) : form.amount,
      contract_id: form.contract_id || (r?.contract_id ?? ''),
      payment_purpose: r
        ? `Оплата за товар згідно з накладною № ${r.document_number ?? ''}${r.document_date ? ' від ' + fmtD(r.document_date) : ''}`
        : form.payment_purpose,
    });
  };
  // Отримувач: контрагент (назва + ЄДРПОУ/РНОКПП) або працівник (ПІБ + РНОКПП + IBAN картки).
  // ч.1301 (власник): контрагента і договір створюють попапом просто з платіжки —
  // як у розпізнаванні виписки і в касових ордерах.
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const pickContractor = (id: string) => {
    const c = (cList.data?.items ?? []).find((x) => x.id === id);
    setForm({ ...form, contractor_id: id, contract_id: '', employee_id: '',
      recipient_name: c ? (c.full_name || c.name) : '',
      recipient_edrpou: c ? (c.edrpou || c.inn || '') : '' });
  };
  const pickEmployee = (id: string) => {
    const e = (empQ.data?.items ?? []).find((x) => x.id === id);
    const iban = (e?.individual_payout_iban ?? '').replace(/\s+/g, '').toUpperCase();
    setForm({ ...form, employee_id: id, contractor_id: '', contract_id: '',
      recipient_name: e?.full_name ?? '', recipient_edrpou: e?.inn ?? '',
      counterparty_iban: iban || form.counterparty_iban,
      counterparty_mfo: iban ? (mfoOf(iban) ?? '') : form.counterparty_mfo });
  };
  // ч.730 (канон): вкладки «Інструкція» (реквізити платіжної інструкції) / «Рахунки обліку».
  const [docTab, setDocTab] = useState<'instr' | 'accounts'>('instr');

  const buildPayload = () => {
    const base = {
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      bank_account_id: form.bank_account_id,
      operation_type: form.operation_type as ReceiptOperationType & PaymentOperationType,
      contractor_id: form.contractor_id || null,
      contract_id: noContract ? null : (form.contract_id || null),
      cash_flow_item_id: form.cash_flow_item_id || null,
      // ч.731: повна картка.
      ...(kind === 'receipt'
        ? { payer_iban: form.counterparty_iban || null, payer_bank_mfo: mfoOf(form.counterparty_iban),
            payer_name: form.recipient_name || null, payer_edrpou: form.recipient_edrpou || null }
        : { recipient_iban: form.counterparty_iban || null, recipient_bank_mfo: mfoOf(form.counterparty_iban),
            recipient_name: form.recipient_name || null, recipient_edrpou: form.recipient_edrpou || null }),
      paid_at: form.paid_at ? new Date(form.paid_at).toISOString() : null,
      department_id: form.department_id || null,
      responsible_name: form.responsible_name || null,
      tax_purpose_id: form.tax_purpose_id || null,
      vat_rate_id: form.vat_rate_id || null,
      vat_amount: form.vat_amount || '0',
      vat_account_code: form.vat_account_code || null,
      vat_account_confirmed_code: form.vat_account_confirmed_code || null,
      // П4a: «сделка» — розрахунки у розрізі замовлень.
      ...(kind === 'receipt'
        ? { customer_order_id: form.order_id || null, goods_sale_id: form.basis_doc_id || null,
            production_service_act_id: form.basis_act_kind === 'psa' ? form.basis_act_id : null,
            processing_services_sale_id: form.basis_act_kind === 'pss' ? form.basis_act_id : null }
        : { supplier_order_id: form.order_id || null, goods_receipt_id: form.basis_doc_id || null }),
      amount: form.amount,
      amount_currency: isFxAccount && form.amount_currency ? form.amount_currency : null,
      exchange_rate: isFxAccount && form.exchange_rate ? form.exchange_rate : null,
      payment_purpose: form.payment_purpose || null,
      corresponding_account: form.corresponding_account || null,
      cash_account_code: form.cash_account_code || null,
      comment: form.comment || null,
      project_id: form.project_id,
    };
    // employee_id — лише для виплати (BankPayment має це поле; BankReceipt — ні).
    if (kind === 'payment') {
      return { ...base, employee_id: form.employee_id || null,
        bank_order_number: form.bank_order_number || null,
        // ч.754: підстава ЗП / податки — link-таблиці (обидва ключі, щоб PATCH замінив).
        payroll_payment_id: isSalary ? (form.basis_hr_id || null) : null,
        tax_remittance_id: isTax ? (form.basis_hr_id || null) : null,
        tax_po_pdfo_id: isSalary ? (form.tax_po_pdfo_id || null) : null,
        tax_po_wz_id: isSalary ? (form.tax_po_wz_id || null) : null,
        tax_po_esv_id: isSalary ? (form.tax_po_esv_id || null) : null };
    }
    return base;
  };

  const create = useMutation({
    mutationFn: () => api.create(buildPayload() as BankReceiptCreate & BankPaymentCreate),
    onSuccess: (doc: { id: string }) => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [itemKey, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    // ч.837 (факт власника): «провести» = спершу ЗБЕРЕГТИ форму (привʼязки ПІ
    // податків, ДДС, призначення…), потім провести — інакше правки губились.
    mutationFn: async () => {
      // SUP-96DD97EE (ч.1266): на НОВОМУ документі — створити, провести, відкрити створений
      // (раніше сюди йшло оновлення з адресою «new»: «doc_id must be valid UUID»).
      if (isNew) {
        return createThenPost(
          () => api.create(buildPayload() as BankReceiptCreate & BankPaymentCreate) as Promise<{ id: string }>,
          (docId) => api.post(docId),
          (doc) => { qc.invalidateQueries({ queryKey: [listKey] }); navigate(`${baseRoute}/${doc.id}`); },
        );
      }
      if (!isPosted) await api.update(id!, buildPayload());
      return api.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(baseRoute);
    },
  });

  const titleNew = kind === 'receipt' ? t('br.new') : t('bp.new');
  const titleList = kind === 'receipt' ? t('br.title') : t('bp.title');
  const counterpartyLabel = kind === 'receipt' ? t('br.payer') : t('bp.recipient');

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    const what = kind === 'receipt' ? 'платіжку вхідну' : 'платіжку вихідну';
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{titleNew}</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {what} у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  // ч.867 (новий дизайн, Закон #10): єдина сторінкова дія — «створити
  // авансову ПН» (2026-08-05: аванс = перша подія, ст.187 ПКУ) — з рядка-
  // посилання над формою переїхала у «Повʼязані дії» правої колонки.
  // Показується лише для надходження від покупця (тип документа/операції);
  // до збереження — disabled з підказкою (Закон #6, ч.702).
  // ч.930 (рішення власника): «створити валютну операцію» — для типу операції
  // «Купівля-продаж валюти»; списання з гривневого рахунку = купівля валюти,
  // з валютного = продаж; надходження — навпаки. Якщо операція вже привʼязана
  // до цього документа — дія неактивна (відкрити її — у блоці «Звʼязані
  // документи», не дублюємо). Показується завжди, активна лише для цього
  // типу операції і збереженого документа (Закон #6, ч.702).
  const isFxOp = form.operation_type === 'CURRENCY_EXCHANGE';
  const fxOpsQ = useQuery({
    queryKey: ['currency-operations', form.organization_id, 'links'],
    queryFn: () => currencyOperations.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: isFxOp && !isNew && !!form.organization_id,
  });
  const linkedFxOp = (fxOpsQ.data?.items ?? []).find((o) => !o.deletion_mark
    && (kind === 'payment' ? o.related_bank_payment_id === id : o.related_bank_receipt_id === id));
  const fxOpKind = kind === 'payment' ? (isFxAccount ? 'SALE' : 'PURCHASE') : (isFxAccount ? 'PURCHASE' : 'SALE');
  const fxAction = {
    label: 'створити валютну операцію',
    title: !isFxOp ? 'Доступно для типу операції «Купівля-продаж валюти»'
      : isNew ? 'Спершу збережіть документ'
      : linkedFxOp ? `Валютна операція № ${linkedFxOp.document_number} уже створена — див. «Звʼязані документи»`
      : `Документ «Купівля/продаж валюти» (${fxOpKind === 'SALE' ? 'продаж' : 'купівля'}) з цим ${kind === 'payment' ? 'списанням' : 'надходженням'} у звʼязаних`,
    disabled: !isFxOp || isNew || !!linkedFxOp,
    onClick: () => navigate(
      `/banking/currency-operations/new?org=${form.organization_id}&op=${fxOpKind}&${kind === 'payment' ? 'bank_payment' : 'bank_receipt'}=${id}`),
  };
  const railActions = [...(advAction ? [advAction] : []), fxAction,
    // ч.1359 (рішення власника): обернений ланцюг з оплати — документи обліку, яких ще немає,
    // створюються з банківського документа із заповненою шапкою; непроведений банк одразу
    // отримує «На підставі» / замовлення, проведений — ручний звʼязок (shared/utils/reverseChain).
    ...(kind === 'receipt' && !isTax && !isEmployeeOp ? [
      { label: 'створити реалізацію',
        title: isNew ? 'Спершу збережіть документ' : 'Документ реалізації з покупцем, договором і датою оплати; оплата рознесеться на нього',
        disabled: isNew, onClick: () => navigate(`/goods-sales/new?bank_receipt=${id}`) },
      { label: 'створити рахунок',
        title: isNew ? 'Спершу збережіть документ' : 'Рахунок покупцю з покупцем і договором цього надходження',
        disabled: isNew, onClick: () => navigate(`/customer-invoices/new?bank_receipt=${id}`) },
      { label: 'створити замовлення покупця',
        title: isNew ? 'Спершу збережіть документ' : 'Замовлення покупця з покупцем і договором цього надходження',
        disabled: isNew, onClick: () => navigate(`/customer-orders/new?bank_receipt=${id}`) },
    ] : []),
    ...(kind === 'payment' && !isTax && !isEmployeeOp ? [
      { label: 'створити надходження',
        title: isNew ? 'Спершу збережіть документ' : 'Надходження товарів і послуг з постачальником, договором і датою оплати; оплата рознесеться на нього',
        disabled: isNew, onClick: () => navigate(`/goods-receipts/new?bank_payment=${id}`) },
      { label: 'створити рахунок постачальника',
        title: isNew ? 'Спершу збережіть документ' : 'Рахунок постачальника з постачальником і договором цього списання',
        disabled: isNew, onClick: () => navigate(`/supplier-invoices/new?bank_payment=${id}`) },
      { label: 'створити замовлення постачальнику',
        title: isNew ? 'Спершу збережіть документ' : 'Замовлення постачальнику з постачальником і договором цього списання',
        disabled: isNew, onClick: () => navigate(`/supplier-orders/new?bank_payment=${id}`) },
    ] : []),
  ];
  // Прогрес — ЛИШЕ обовʼязкові поля (ті, що позначені « *»).
  const partyFilled = isTax ? !!form.recipient_name
    : isEmployeeOp ? !!form.employee_id : !!form.contractor_id;
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'account', title: 'Рахунок і операція', required: 2,
      filled: (form.bank_account_id ? 1 : 0) + (form.operation_type ? 1 : 0) },
    { id: 'instr', title: kind === 'receipt' ? 'Платник і сума' : 'Отримувач і сума',
      required: 2 + (isTax ? 1 : 0) + (isSalary ? TAX_LINK_TYPES.length : 0) + (isFxAccount ? 1 : 0),
      filled: (partyFilled ? 1 : 0) + (Number(form.amount) > 0 ? 1 : 0)
        + (isTax && taxValue ? 1 : 0)
        + (isSalary ? TAX_LINK_TYPES.filter((t) => !!form[taxLinkField(t)]).length : 0)
        + (isFxAccount && Number(form.amount_currency) > 0 ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? titleNew : `${titleList}: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          // ч.794: зі «збережених» — повертаємось на ту саму вкладку (і для непроведеного списання).
          onBackToList={() => { const from = sp.get('from'); navigate(from && from !== 'paid' ? `${baseRoute}?tab=${from}` : (!isNew && !isPosted) ? `${baseRoute}?tab=saved` : baseRoute); }}
          onSave={() => {
            if (!form.operation_type) { setError('Оберіть тип операції'); return; }
            if (isNew) create.mutate(); else update.mutate();
          }}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => {
            if (isNew && !form.operation_type) { setError('Оберіть тип операції'); return; }
            post.mutate();
          }}
          postPending={post.isPending}
          postDisabled={!(Number(form.amount) > 0)}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          entriesDoc={!isNew && id
            ? { type: kind === 'receipt' ? 'doc.bank_receipts' : 'doc.bank_payments', id }
            : undefined}
          extraStatusBadges={form.paid_at ? [{
            label: `оплачено ${new Date(form.paid_at).toLocaleDateString('uk-UA')}`, tone: 'green',
            title: 'Дата оплати — з банківської виписки',
          }] : []}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`${baseRoute}/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: kind === 'receipt' ? 'doc.bank_receipts' : 'doc.bank_payments', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('bd.organization')} *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, bank_account_id: '' })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{kind === 'payment' ? '№ ПІ' : t('bd.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && nextNumQ.data ? `буде: ${nextNumQ.data.next_number}` : ''}
                 title={kind === 'payment' ? 'Номер платіжки — єдиний для нас і банку; наступний за всіма сплаченими та збереженими' : undefined}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('bd.date')} *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Рахунок і операція">
      {/* ч.754 (рішення власника): рядок 2 — IBAN платника | Тип операції | Стаття ДДС. */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>{kind === 'payment' ? 'IBAN платника' : t('bd.bank_account')} *</label>
          <select value={form.bank_account_id}
                  onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {baQ.data?.map((b) => (
              <option key={b.id} value={b.id}>{b.iban} {b.account_name ? '— ' + b.account_name : ''}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('bd.operation_type')} *</label>
          <select value={form.operation_type}
                  onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                  disabled={isPosted}>
            <option value="">— оберіть тип операції —</option>
            {visibleOps.map((op) => (
              <option key={op} value={op}>{t(`bd.ops.${op}`)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label title="Стаття руху грошових коштів (звіт ДДС) — авто за типом операції, можна змінити. Тип операції визначає проводку, стаття — рядок звіту про рух коштів.">
            Стаття ДДС
          </label>
          <SearchSelect
            items={cfiItems}
            value={form.cash_flow_item_id}
            onChange={(id) => setForm({ ...form, cash_flow_item_id: id })}
            disabled={isPosted}
          />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title={kind === 'receipt' ? 'Платник і сума' : 'Отримувач і сума'}>
      {/* ч.730: вкладки за каноном — «Інструкція» зліва, «Рахунки обліку» справа. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={docTab === 'instr' ? 'active' : ''}
            onClick={() => setDocTab('instr')}>Інструкція</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={docTab === 'accounts' ? 'active' : ''}
            onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>
      {docTab === 'instr' && (<>
      {/* Рядок 3: Отримувач (контрагент / працівник для ЗП) | Код отримувача | IBAN отримувача. */}
      <div className="doc-grid">
        {isTax && (
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>
              Податок *
              {!isPosted && !!currentOrg && (
                <a style={{ marginLeft: 8, fontSize: '0.82rem', fontWeight: 'normal', color: 'var(--primary)', cursor: 'pointer' }}
                  title="Податкові рахунки з картки організації: обрати або створити новий"
                  onClick={() => setTaxPickerOpen(true)}>додати</a>
              )}
            </label>
            <select value={taxValue} disabled={isPosted}
                    onChange={(e) => pickTax(taxItems.find((x) => x.key === e.target.value))}>
              <option value="">—</option>
              {taxItems.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ gridColumn: isTax ? 'span 3' : 'span 5' }}>
          <label>{isEmployeeOp ? 'Отримувач (працівник)' : counterpartyLabel} *
            {!isTax && !isEmployeeOp && (
              <QuickCreateLink disabled={isPosted} title="Створити нового контрагента"
                onClick={() => setQuickContractor(true)} />
            )}
          </label>
          {isTax ? (
            <input value={form.recipient_name} maxLength={500} disabled={isPosted}
                   placeholder="ГУ ДПС у …"
                   onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
          ) : isEmployeeOp ? (
            <SearchSelect
              items={(empQ.data?.items ?? []).map((e) => ({
                id: e.id, label: e.full_name ?? e.code ?? e.id,
                hint: e.is_active === false ? 'звільнений' : (e.inn ?? null),
              }))}
              value={form.employee_id}
              placeholder="— працівник (у т.ч. звільнений) —"
              onChange={pickEmployee}
              disabled={isPosted}
            />
          ) : (
            <SearchSelect
              items={((cList.data?.items ?? [])).map((c) => ({
                id: c.id, label: c.name, hint: c.edrpou ?? c.inn ?? null,
              }))}
              value={form.contractor_id}
              onChange={pickContractor}
              disabled={isPosted}
            />
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          {/* ч.842 (рішення власника): код — лише з картки контрагента/працівника, не редагується. */}
          <label title={kind === 'receipt'
            ? 'ЄДРПОУ / РНОКПП платника — з картки контрагента'
            : 'ЄДРПОУ / РНОКПП отримувача — з картки контрагента/працівника, у платіжну інструкцію та файл для банку'}>
            {kind === 'receipt' ? 'Код платника' : 'Код отримувача'}
          </label>
          <input value={form.recipient_edrpou} readOnly disabled
                 title="Підтягується з картки контрагента / працівника" />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            {kind === 'receipt' ? 'IBAN платника' : 'IBAN отримувача'}
            {counterpartyBankName && (
              <small style={{ color: 'var(--muted)', marginLeft: 6 }}
                title="Назва банку з довідника за МФО IBAN">{counterpartyBankName}</small>
            )}
            {!!form.contractor_id && !isPosted && (
              <a style={{ marginLeft: 8, fontSize: '0.82rem', fontWeight: 'normal', color: 'var(--primary)', cursor: 'pointer' }}
                title="Рахунки з картки контрагента: обрати або додати новий (збережеться у картку)"
                onClick={() => setBankPickerOpen(true)}>рахунки контрагента</a>
            )}
          </label>
          <input value={form.counterparty_iban} maxLength={34}
                 onChange={(e) => setForm({ ...form, counterparty_iban: e.target.value,
                   counterparty_mfo: mfoOf(e.target.value) ?? '' })}
                 disabled={isPosted} placeholder="UA…" />
        </div>
      </div>

      {/* Рядок 4: Договір (не для податків/ЗП) | На підставі | (ЗП) ПДФО | ВЗ | ЄСВ. */}
      <div className="doc-grid">
        {!noContract && (
          <div className="field" style={{ gridColumn: 'span 5' }}>
            <label>{t('bd.contract')}
              <QuickCreateLink disabled={isPosted || !form.contractor_id}
                title={form.contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
                onClick={() => setQuickContract(true)} />
            </label>
            <select value={form.contract_id}
                    onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                    disabled={isPosted || !form.contractor_id}>
              <option value="">—</option>
              {contractsQ.data?.map((c) => (
                <option key={c.id} value={c.id}>{contractLabel(c)}</option>
              ))}
            </select>
          </div>
        )}
        <div className="field" style={{ gridColumn: isSalary ? 'span 5' : noContract ? 'span 12' : 'span 7' }}>
          <label title={isSalary || isTax
            ? 'Неоплачені документи Кадрів: відомості виплати ЗП / виплати податків'
            : 'Відкриті замовлення/рахунки та проведені неоплачені накладні контрагента; вибір підставляє суму, договір і призначення'}>
            На підставі
          </label>
          <SearchSelect
            items={(isSalary || isTax) ? hrBasisItems : basisItems}
            value={basisValue}
            placeholder={(isSalary || isTax) || form.contractor_id ? '— без підстави —' : '— спершу оберіть отримувача —'}
            onChange={pickBasis}
            disabled={isPosted || (!(isSalary || isTax) && !form.contractor_id)}
          />
        </div>
        {isSalary && TAX_LINK_TYPES.map((t) => (
          <div className="field" key={t} style={{ gridColumn: t === 'ESV' ? 'span 3' : 'span 2' }}>
            <label>
              {TAX_LINK_LABEL[t]} *
              {!isPosted && !!form.organization_id && (
                <a style={{ marginLeft: 8, fontSize: '0.82rem', fontWeight: 'normal', color: 'var(--primary)', cursor: 'pointer' }}
                  title="Сплачені платіжки податків (пошук, період) — якщо податки сплачено раніше"
                  onClick={() => setTaxPoPicker(t)}>обрати</a>
              )}
            </label>
            <SearchSelect
              items={taxItemsFor(t)}
              value={form[taxLinkField(t)]}
              placeholder="оберіть ПІ"
              onChange={(id) => pickTaxPo(t, id ? ((taxCandQ.data ?? []).find((c) => c.id === id) ?? taxPicked[id] ?? null) : null)}
              disabled={isPosted}
            />
          </div>
        ))}
      </div>

      {/* ч.798: валютний рахунок — сума у валюті + курс → сума у грн (регістри/проводки в грн). */}
      {isFxAccount && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Сума у валюті ({accountCurrency}) *</label>
            <input type="number" step="0.01" value={form.amount_currency}
                   onChange={(e) => setForm({ ...form, amount_currency: e.target.value })}
                   disabled={isPosted} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Курс (грн за 1 {accountCurrency})</label>
            <input type="number" step="0.0001" value={form.exchange_rate}
                   onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                   disabled={isPosted} />
          </div>
        </div>
      )}
      {/* Рядок 5: Сума | Ставка ПДВ (за замовч. 20%) | Сума ПДВ (авто). */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{isFxAccount ? 'Сума, грн (еквівалент)' : t('bd.amount')} *</label>
          <input type="number" step="0.01" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Ставка ПДВ</label>
          <select value={form.vat_rate_id}
                  onChange={(e) => setForm({ ...form, vat_rate_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {(vatQ.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Сума ПДВ</label>
          <input type="number" step="0.01" value={form.vat_amount}
                 onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      <div className="field">
        <label>{t('bd.purpose')}</label>
        <textarea value={form.payment_purpose}
                  onChange={(e) => setForm({ ...form, payment_purpose: e.target.value })}
                  disabled={isPosted}
                  rows={2} />
      </div>
      {/* Поля з імпорту/виписки — лише коли заповнені (№ у банку, підрозділ, відповідальний). */}
      {(form.bank_order_number || form.department_id || form.responsible_name) && (
        <div className="doc-grid">
          {kind === 'payment' && form.bank_order_number && (
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label title="Номер платіжної інструкції у банку (з виписки / 1С)">№ у банку</label>
              <input value={form.bank_order_number} disabled />
            </div>
          )}
          {form.department_id && (
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Підрозділ</label>
              <select value={form.department_id}
                      onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                      disabled={isPosted}>
                <option value="">—</option>
                {(depQ.data?.items ?? []).filter((d) => !d.deletion_mark).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
          {form.responsible_name && (
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Відповідальний</label>
              <input value={form.responsible_name} maxLength={255}
                     onChange={(e) => setForm({ ...form, responsible_name: e.target.value })}
                     disabled={isPosted} />
            </div>
          )}
        </div>
      )}
      {taxPoPicker && form.organization_id && (
        <TaxPoPicker
          organizationId={form.organization_id}
          taxType={taxPoPicker}
          onPick={(c) => pickTaxPo(taxPoPicker, c)}
          onClose={() => setTaxPoPicker(null)}
        />
      )}
      {taxPickerOpen && currentOrg && (
        <OrgTaxAccountPicker
          organization={currentOrg}
          onPick={pickTax}
          onClose={() => setTaxPickerOpen(false)}
        />
      )}
      {bankPickerOpen && form.contractor_id && (
        <ContractorBankPicker
          contractorId={form.contractor_id}
          onPick={(iban) => setForm((f) => ({ ...f, counterparty_iban: iban, counterparty_mfo: mfoOf(iban) ?? '' }))}
          onClose={() => setBankPickerOpen(false)}
        />
      )}
      </>)}

      {/* Рахунки обліку (2026-08-09, рішення власника): порожні поля = авто. */}
      {docTab === 'accounts' && (
        <div className="doc-grid" style={{ marginTop: '0.5rem' }}>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="Кореспондуючий рахунок проводки (порожньо — авто за типом операції: 361/631/685/…)">
              Кореспондуючий рахунок</label>
            <input value={form.corresponding_account} maxLength={10}
              onChange={(e) => setForm({ ...form, corresponding_account: e.target.value })}
              disabled={isPosted}
              placeholder="авто за типом операції" />
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="Рахунок грошових коштів (порожньо — авто: 311; напр. 313 для спецрахунку)">
              Рахунок грошових коштів</label>
            <input value={form.cash_account_code} maxLength={10}
              onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })}
              disabled={isPosted}
              placeholder="авто: 311" />
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="Рахунок ПДВ непідтвердженого (1С: Счет НДС, напр. 6442)">Рахунок ПДВ (непідтв.)</label>
            <input value={form.vat_account_code} maxLength={10}
              onChange={(e) => setForm({ ...form, vat_account_code: e.target.value })}
              disabled={isPosted} placeholder="6442" />
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="Рахунок ПДВ підтвердженого (1С: Счет НДС н/к, напр. 6441)">Рахунок ПДВ (підтв.)</label>
            <input value={form.vat_account_confirmed_code} maxLength={10}
              onChange={(e) => setForm({ ...form, vat_account_confirmed_code: e.target.value })}
              disabled={isPosted} placeholder="6441" />
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label title="Податкове призначення (1С: Нал. назначение)">Податкове призначення</label>
            <select value={form.tax_purpose_id}
                    onChange={(e) => setForm({ ...form, tax_purpose_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">—</option>
              {(tpQ.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name_uk}</option>)}
            </select>
          </div>
          {!isNew && (docQuery.data as { operation_kind_1c?: string | null } | undefined)?.operation_kind_1c && (
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Вид операції в 1С</label>
              <input value={(docQuery.data as { operation_kind_1c?: string | null }).operation_kind_1c ?? ''} disabled />
            </div>
          )}
        </div>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        {/* ч.867: поле коментаря — раніше state/payload були, а input у формі не було. */}
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={kind === 'receipt' ? 'doc.bank_receipts' : 'doc.bank_payments'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
      {/* ч.1301: попапи швидкого створення просто з форми платіжки. */}
      {quickContractor && (
        <QuickContractorModal
          direction={kind === 'receipt' ? 'IN' : 'OUT'}
          onClose={() => setQuickContractor(false)}
          onCreated={async (cid) => {
            setQuickContractor(false);
            // нового ще немає у списку (він перезавантажується) — реквізити з картки
            let c: { name: string; full_name?: string | null; edrpou?: string | null; inn?: string | null } | undefined;
            try { c = await contractorsApi.get(cid); } catch { c = undefined; }
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '', employee_id: '',
              recipient_name: c ? (c.full_name || c.name) : f.recipient_name,
              recipient_edrpou: c ? (c.edrpou || c.inn || '') : f.recipient_edrpou }));
            invalidateRefLists(qc, 'contractors');
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={(cList.data?.items ?? []).find((c) => c.id === form.contractor_id)?.name ?? form.recipient_name}
          direction={kind === 'receipt' ? 'IN' : 'OUT'}
          operationType={form.operation_type}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            invalidateRefLists(qc, 'contracts');
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
